import React, { useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/fr';
import { Meal } from '../../types';
import { MEAL_CATEGORIES, FAMILY_MEMBERS } from '../../utils/constants';

moment.locale('fr');

const CATEGORY_COLORS: Record<string, { border: string; bg: string; text: string; headerBg: string }> = {
  'Repas rapide':      { border: '#0097a7', bg: '#e0f7fa', text: '#006064', headerBg: '#0097a7' },
  'Dîner familial':    { border: '#f9a825', bg: '#fffde7', text: '#e65100', headerBg: '#f9a825' },
  'Occasion spéciale': { border: '#e91e63', bg: '#fce4ec', text: '#880e4f', headerBg: '#e91e63' },
  'Restes':            { border: '#558b2f', bg: '#f1f8e9', text: '#33691e', headerBg: '#558b2f' },
  'Restaurant':        { border: '#e64a19', bg: '#fbe9e7', text: '#bf360c', headerBg: '#e64a19' },
};

const DEFAULT_COLORS = { border: '#607D8B', bg: '#f5f5f5', text: '#37474f', headerBg: '#607D8B' };

const getCategoryEmoji = (category: string) =>
  MEAL_CATEGORIES.find(c => c.name === category)?.emoji ?? '🍽️';

const getPersonColor = (name: string) =>
  FAMILY_MEMBERS.find(m => m.name === name)?.color ?? '#9ca3af';

interface MealPreviewProps {
  meal: Meal | null;
  onClose: () => void;
}

const MealPreview: React.FC<MealPreviewProps> = ({ meal, onClose }) => {
  useEffect(() => {
    if (!meal) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', onKey);
    };
  }, [meal, onClose]);

  if (!meal) return null;

  const colors = CATEGORY_COLORS[meal.category] ?? DEFAULT_COLORS;
  const emoji = getCategoryEmoji(meal.category);
  const dateLabel = moment(meal.dateString).format('dddd D MMMM YYYY');
  const displayName = meal.name && meal.name !== meal.category ? meal.name : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />

      {/* Card */}
      <div
        className="relative w-full md:max-w-sm md:mx-4 rounded-t-3xl md:rounded-3xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: '#fff' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Colored header band */}
        <div
          className="relative px-5 pt-5 pb-6"
          style={{ backgroundColor: colors.headerBg }}
        >
          {/* Drag handle on mobile */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-white/40 md:hidden" />

          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>

          {/* Emoji + category */}
          <div className="flex items-center gap-3 mt-2">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-3xl flex-shrink-0">
              {emoji}
            </div>
            <div>
              <div className="text-white/70 text-xs font-semibold uppercase tracking-widest">
                {meal.category}
              </div>
              {displayName && (
                <div className="text-white text-xl font-bold leading-tight mt-0.5">
                  {displayName}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">

          {/* Date */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.bg }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={colors.border} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/>
                <path d="M16 2v4M8 2v4M3 10h18"/>
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-700 capitalize">{dateLabel}</span>
          </div>

          {/* People */}
          {meal.people && meal.people.length > 0 && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: colors.bg }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={colors.border} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {meal.people.map(person => (
                  <span
                    key={person}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: getPersonColor(person) }}
                  >
                    {person}
                  </span>
                ))}
                {meal.peopleCount && meal.peopleCount > meal.people.length && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                    +{meal.peopleCount - meal.people.length} invité{meal.peopleCount - meal.people.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Location */}
          {meal.location && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.bg }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={colors.border} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <span className="text-sm text-gray-700">{meal.location}</span>
            </div>
          )}

          {/* Description */}
          {meal.description && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: colors.bg }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={colors.border} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{meal.description}</p>
            </div>
          )}

        </div>

        {/* Bottom safe area on mobile */}
        <div className="h-safe-bottom pb-4 md:pb-0" />
      </div>
    </div>
  );
};

export default MealPreview;
